import { SpecraBadge, SpecraUnverifiedClaim } from '@mono/ui-components';
import { createElement } from 'react';
import { formatMoneyCompact } from '../../../lib/format/money';
import { entityHref } from '../../Capture/record/entityRoutes';
import { dateToYear, holderLabel, monthYear, roundEnd, toYear, yearOf } from './seatTime';
export const SEAT_LENSES = [
    'everything',
    'ours',
    'competitors',
    'people'
];
const EVENT_TONE = {
    market: 'cat-1',
    work: 'cat-6',
    decision: 'cat-5',
    delivery: 'ours',
    theirs: 'theirs',
    loss: 'legacy',
    people: 'cat-1',
    lesson: 'cat-1'
};
const HOLLOW = new Set([
    'delivery',
    'theirs',
    'people',
    'lesson'
]);
const LABELLED = new Set([
    'decision'
]);
export const eventTone = (kind)=>EVENT_TONE[kind] ?? 'other';
const money = (cents)=>formatMoneyCompact(cents);
const isOpen = (p)=>p.outcome === 'open' || !p.endedAt;
const current = (s)=>s.rounds.find((r)=>r.status === 'current');
/** Claims keyed by the seat they are about; a claim on a seat merged away shows on the seat that absorbed it. */ export function claimsBySeat(seats, claims) {
    const byId = new Map(seats.map((s)=>[
            s.id,
            s
        ]));
    const target = (id)=>{
        let cur = id;
        for(let i = 0; i < 10; i++){
            const next = byId.get(cur)?.mergedIntoSeatId;
            if (!next || !byId.has(next)) break;
            cur = next;
        }
        return cur;
    };
    const out = {};
    for (const c of claims){
        const seatIds = new Set(c.links.filter((l)=>l.targetKind === 'MARKET_SEAT' && byId.has(l.targetId)).map((l)=>target(l.targetId)));
        for (const id of seatIds)out[id] = [
            ...out[id] ?? [],
            c
        ];
    }
    return out;
}
/** The OR-ed link filters that read hearsay for every seat on the landscape (REG expands merge chains). */ export function seatHearsayLinks(seats) {
    return seats.slice(0, 50).map((s)=>({
            linkRole: 'RELEVANT_TO',
            targetKind: 'MARKET_SEAT',
            targetId: s.id
        }));
}
function segmentsFor(seat, now, allRounds) {
    const cur = current(seat);
    const heldNow = new Set(allRounds.filter((r)=>r.status === 'current').map((r)=>r.holderCompanyId || holderLabel(r)));
    const out = [];
    for (const r of seat.rounds){
        const start = toYear(r.popStart);
        const end = toYear(roundEnd(r));
        const mods = r.modsValueCents > BigInt(0) ? ` + ${money(r.modsValueCents)} mod` : '';
        const value = r.valueCents > BigInt(0) ? money(r.valueCents) : '';
        const holder = holderLabel(r);
        if (r.status === 'future') {
            const to = toYear(r.rfpExpectedOn) ?? end ?? now + 0.5;
            out.push({
                id: r.id,
                start: now,
                end: Math.max(to, now + 1 / 12),
                label: r.label ? `New seat · ${r.label}` : 'next',
                tone: 'legacy',
                pattern: 'hatched'
            });
            continue;
        }
        if (start === null) continue;
        const isUs = r.holderKind === 'us';
        const label = isUs ? `${seat.name} · us${value ? ` · ${value}` : ''}` : `${holder}${value ? ` · ${value}` : ''}${mods}`;
        const href = isUs && r.programId ? entityHref('program', r.programId) ?? undefined : undefined;
        if (r === cur) {
            const last = end ?? start;
            const pastEnd = Math.min(now, last);
            const [past, future] = isUs ? [
                'ours',
                'ours-future'
            ] : [
                'theirs',
                'theirs-future'
            ];
            if (pastEnd > start) {
                out.push({
                    id: `${r.id}:past`,
                    start,
                    end: pastEnd,
                    label,
                    tone: past,
                    href
                });
            }
            if (last > now) {
                const futureLabel = isUs ? `PoP to ${monthYear(last)}` : `options to ${yearOf(roundEnd(r)) ?? ''}`;
                out.push({
                    id: `${r.id}:future`,
                    start: Math.max(now, start),
                    end: last,
                    label: futureLabel,
                    tone: future,
                    href
                });
            }
            continue;
        }
        if (end === null) continue;
        const sameAsNow = heldNow.has(r.holderCompanyId || holder);
        out.push({
            id: r.id,
            start,
            end,
            label: sameAsNow ? label : `${holder} (legacy)`,
            tone: sameAsNow ? 'theirs' : 'legacy',
            href
        });
    }
    return out;
}
function sublabelFor(seat) {
    const cur = current(seat);
    const future = seat.rounds.find((r)=>r.status === 'future');
    const first = seat.rounds[0];
    if (seat.splitFromSeatId && first?.status === 'future' && toYear(seat.splitOn) !== null) {
        return `New seat · split ${monthYear(toYear(seat.splitOn))}`;
    }
    const opens = future ? toYear(future.rfpExpectedOn) : null;
    if (cur && opens !== null) return `${holderLabel(cur)} · opens ~${monthYear(opens)}`;
    const ends = cur ? toYear(roundEnd(cur)) : null;
    if (cur && ends !== null) return `${holderLabel(cur)} · ends ${monthYear(ends)}`;
    return undefined;
}
function threadsFor(seat, now, rounds, splitChildren) {
    const out = [];
    for (const p of seat.pursuits){
        const from = toYear(p.startedAt);
        if (from === null) continue;
        const to = Math.max(toYear(p.endedAt) ?? now, from);
        const dead = p.outcome === 'lost' || p.outcome === 'no_bid';
        const tone = dead ? 'legacy' : 'cat-6';
        const suffix = p.outcome === 'won' ? ' · won' : p.outcome === 'lost' ? ' · lost' : p.labels[0] ? ` · ${p.labels[0]}` : undefined;
        out.push({
            id: `${seat.id}:${p.captureProjectId}`,
            from,
            to,
            style: p.relation === 'recompete' ? 'dashed' : 'solid',
            tone,
            label: p.name,
            sublabel: suffix,
            connectTo: splitChildren.get(p.captureProjectId)
        });
        const opens = toYear(rounds.get(p.roundId)?.rfpExpectedOn ?? '');
        if (isOpen(p) && opens !== null && opens > now) {
            out.push({
                id: `${seat.id}:${p.captureProjectId}:next`,
                from: now,
                to: opens,
                style: 'dotted',
                tone
            });
        }
    }
    return out;
}
function stationFor(e, at, selectedId) {
    const selected = e.id === selectedId;
    return {
        id: e.id,
        at,
        tone: eventTone(e.kind),
        hollow: HOLLOW.has(e.kind),
        title: `${e.title} · ${monthYear(at)}`,
        label: LABELLED.has(e.kind) || selected ? e.title : undefined
    };
}
function dimmedFor(seat, lens) {
    if (lens === 'ours') return !seat.rounds.some((r)=>r.holderKind === 'us') && seat.pursuits.length === 0;
    if (lens === 'competitors') return !seat.rounds.some((r)=>r.holderKind === 'competitor');
    return lens === 'people';
}
/** GRW-25 item 5: the seat response as SpecraSwimlaneTimeline props. Pure; `now` is injected. */ export function buildSeatTimeline(resp, now, lens, selected = {}, opts = {}) {
    const nowT = dateToYear(now);
    const seats = resp.seats;
    const allRounds = seats.flatMap((s)=>s.rounds);
    const roundById = new Map(allRounds.map((r)=>[
            r.id,
            r
        ]));
    const marks = [];
    const ends = [
        nowT + 1
    ];
    for (const r of allRounds){
        const s = toYear(r.popStart);
        if (s !== null && r.status !== 'future') marks.push(s);
        for (const d of [
            r.optionsEnd,
            r.rfpExpectedOn,
            r.popEnd
        ]){
            const t = toYear(d);
            if (t !== null) ends.push(t);
        }
    }
    for (const s of seats){
        for (const e of s.events){
            const t = toYear(e.occurredAt);
            if (t !== null) marks.push(t);
        }
        for (const p of s.pursuits){
            const t = toYear(p.startedAt);
            if (t !== null) marks.push(t);
        }
    }
    for (const m of resp.market){
        const t = toYear(m.month);
        if (t !== null) marks.push(t);
    }
    const start = Math.floor(Math.min(nowT, ...marks) + 1e-9);
    const end = Math.max(Math.ceil(Math.max(...ends) * 12 - 1e-6) / 12, start + 1);
    const cutoff = nowT - 3;
    const periods = [];
    for(let y = start; y < end; y++)periods.push({
        id: `y${y}`,
        label: String(y),
        start: y,
        end: y + 1
    });
    // A pursuit split from another seat's pursuit draws the parent's elbow to the child lane.
    const pursuitSeat = new Map();
    for (const s of seats)for (const p of s.pursuits)if (!pursuitSeat.has(p.captureProjectId)) pursuitSeat.set(p.captureProjectId, s.id);
    const splitChildren = new Map();
    for (const s of seats){
        for (const p of s.pursuits){
            const parent = p.splitFromCaptureProjectId;
            const at = toYear(p.startedAt);
            if (parent && at !== null && pursuitSeat.get(parent) && pursuitSeat.get(parent) !== s.id) {
                splitChildren.set(parent, {
                    laneId: s.id,
                    at
                });
            }
        }
    }
    const lanes = seats.map((seat)=>{
        const mergedAt = seat.mergedIntoSeatId ? toYear(seat.mergedOn) : null;
        const survivor = seats.find((s)=>s.id === seat.mergedIntoSeatId);
        const mergeLabel = `Merged into ${survivor?.name ?? seat.mergedIntoSeatId} · ${mergedAt === null ? '—' : monthYear(mergedAt)}`;
        const arrival = (claim)=>{
            const times = claim.links.filter((l)=>l.targetKind === 'MARKET_SEAT').flatMap((link)=>{
                let source = seats.find((s)=>s.id === link.targetId);
                let at = Number.NEGATIVE_INFINITY;
                for(let i = 0; source && i < 10; i++){
                    if (source.id === seat.id) return [
                        at
                    ];
                    if (!source.mergedIntoSeatId) break;
                    at = Math.max(at, toYear(source.mergedOn) ?? Number.NEGATIVE_INFINITY);
                    source = seats.find((s)=>s.id === source?.mergedIntoSeatId);
                }
                return [];
            });
            return Math.max(claim.heardOn ? dateToYear(claim.heardOn) : nowT, times.length ? Math.min(...times) : Number.NEGATIVE_INFINITY);
        };
        const cur = current(seat);
        const hasOpen = seat.pursuits.some(isOpen);
        const curEnd = cur ? toYear(roundEnd(cur)) : null;
        const annotations = (opts.hearsay?.[seat.id] ?? []).map((c)=>({
                id: `hearsay:${c.id}`,
                at: arrival(c),
                row: 'below',
                node: createElement(SpecraUnverifiedClaim, {
                    compact: true,
                    claim: c.statement,
                    kindLabel: 'Hearsay',
                    onActivate: ()=>opts.onActivateClaim?.(c.id)
                })
            }));
        if (mergedAt !== null) annotations.push({
            id: `merge:${seat.id}`,
            at: mergedAt,
            row: 'thread',
            node: mergeLabel
        });
        if (!seat.mergedIntoSeatId && cur && cur.holderKind !== 'us' && curEnd !== null && curEnd <= nowT + 2 && !hasOpen) {
            annotations.push({
                id: `no-pursuit:${seat.id}`,
                at: nowT,
                row: 'thread',
                node: createElement(SpecraBadge, {
                    variant: 'danger'
                }, 'No pursuit on this seat')
            });
        }
        const markers = seat.rounds.flatMap((r)=>{
            const at = r.status === 'future' ? toYear(r.rfpExpectedOn) : null;
            return at === null ? [] : [
                {
                    id: `${r.id}:rfp`,
                    at,
                    kind: 'rfp-expected',
                    title: `Next RFP expected · ${monthYear(at)}`
                }
            ];
        });
        const stations = seat.events.flatMap((e)=>{
            const at = toYear(e.occurredAt);
            return at === null ? [] : [
                stationFor(e, at, selected.stationId)
            ];
        });
        const segments = segmentsFor(seat, nowT, allRounds);
        // Keep each round on its original seat; project its post-merge portion on the survivor.
        for (const loser of seats.filter((s)=>s.mergedIntoSeatId === seat.id)){
            const at = toYear(loser.mergedOn);
            if (at === null) continue;
            segments.push(...segmentsFor(loser, nowT, allRounds).map((s)=>({
                    ...s,
                    id: `${loser.id}:${s.id}`,
                    start: Math.max(s.start, at)
                })).filter((s)=>s.end > s.start));
        }
        const threads = threadsFor(seat, nowT, roundById, splitChildren).map((t)=>mergedAt === null ? t : {
                ...t,
                to: Math.min(t.to, mergedAt)
            }).filter((t)=>t.to >= t.from);
        if (mergedAt !== null && survivor) threads.push({
            id: `merge:${seat.id}`,
            from: mergedAt,
            to: mergedAt,
            style: 'solid',
            tone: 'legacy',
            connectTo: {
                laneId: survivor.id,
                at: mergedAt
            }
        });
        return {
            id: seat.id,
            label: seat.name,
            sublabel: sublabelFor(seat),
            segments: segments.map((s)=>mergedAt === null ? s : {
                    ...s,
                    end: Math.min(s.end, mergedAt)
                }).filter((s)=>s.end > s.start),
            threads,
            stations,
            markers,
            annotations,
            dimmed: !!seat.mergedIntoSeatId || dimmedFor(seat, lens)
        };
    });
    const bands = [];
    if (resp.market.length > 0) {
        bands.push({
            id: 'market',
            label: `Market · ${resp.marketTotal} notices`,
            kind: 'density',
            dots: resp.market.flatMap((m)=>{
                const at = toYear(m.month);
                return at === null ? [] : [
                    {
                        id: `market:${m.month}`,
                        at: at + 0.5 / 12,
                        tone: 'cat-1',
                        title: `${m.count} notices · ${monthYear(at)}`
                    }
                ];
            })
        });
    }
    if (lanes.length > 0) bands.push({
        id: 'seats',
        label: '',
        kind: 'lanes',
        lanes
    });
    const legend = [
        {
            label: 'Contract we hold',
            tone: 'ours'
        },
        {
            label: 'Competitor holds',
            tone: 'theirs'
        },
        {
            label: 'Next round, not yet awarded',
            tone: 'legacy',
            shape: 'hatch'
        },
        {
            label: 'Our pursuit',
            tone: 'cat-6',
            shape: 'line'
        },
        {
            label: 'Pursuit while we hold the seat',
            tone: 'cat-6',
            shape: 'dashed-line'
        },
        {
            label: 'Next RFP expected',
            tone: 'ink',
            shape: 'diamond'
        }
    ];
    const rows = seats.flatMap((s)=>[
            ...s.rounds.map((r)=>({
                    id: r.id,
                    cells: {
                        seat: s.name,
                        kind: `Round · ${r.status}`,
                        from: r.popStart,
                        to: roundEnd(r),
                        who: holderLabel(r),
                        value: r.valueCents > BigInt(0) ? money(r.valueCents) : ''
                    }
                })),
            ...s.events.map((e)=>({
                    id: e.id,
                    cells: {
                        seat: s.name,
                        kind: e.kind,
                        from: e.occurredAt,
                        to: '',
                        who: e.title,
                        value: ''
                    }
                }))
        ]);
    return {
        title: 'Seat history',
        scale: {
            start,
            end,
            now: nowT,
            stops: start < cutoff ? [
                {
                    at: start,
                    weight: 0.42
                },
                {
                    at: cutoff,
                    weight: 1
                }
            ] : undefined
        },
        periods,
        selectedPeriodId: selected.periodId ?? `y${Math.floor(nowT)}`,
        selectedStationId: selected.stationId,
        nowLabel: `Now · ${monthYear(nowT)}`,
        bands,
        legend,
        table: {
            columns: [
                {
                    key: 'seat',
                    label: 'Seat'
                },
                {
                    key: 'kind',
                    label: 'Kind'
                },
                {
                    key: 'from',
                    label: 'From'
                },
                {
                    key: 'to',
                    label: 'To'
                },
                {
                    key: 'who',
                    label: 'Holder/Title'
                },
                {
                    key: 'value',
                    label: 'Value',
                    numeric: true
                }
            ],
            rows
        },
        emptyMessage: 'No seats yet. Use Manage seats to add the needs this market keeps buying.'
    };
}
